"use client"

import { useState } from 'react'
import { Button } from '@/components/ui/button'

const PLATFORMS = [
  { id: 'web', name: 'Web Uygulaması (SaaS / Portal)', basePrice: 5000 },
  { id: 'mobile', name: 'Mobil Uygulama (iOS & Android)', basePrice: 8000 },
  { id: 'ecommerce', name: 'Özel E-Ticaret / Pazar Yeri', basePrice: 6000 },
  { id: 'corporate', name: 'Kurumsal Web Sitesi', basePrice: 2000 },
]

const FEATURES = [
  { id: 'auth', name: 'Kullanıcı Girişi / Kayıt', price: 500 },
  { id: 'payments', name: 'Online Ödeme (Stripe, Iyzico vb.)', price: 800 },
  { id: 'admin', name: 'Özel Yönetim Paneli', price: 1500 },
  { id: 'chat', name: 'Canlı Destek / Mesajlaşma', price: 1000 },
  { id: 'api', name: 'Harici API Entegrasyonu', price: 800 },
]

export function CostCalculator() {
  const [platform, setPlatform] = useState(PLATFORMS[0])
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([])
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const toggleFeature = (id: string) => {
    setSelectedFeatures(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    )
  }

  const totalCost = platform.basePrice + selectedFeatures.reduce((acc, curr) => {
    const f = FEATURES.find(x => x.id === curr)
    return acc + (f ? f.price : 0)
  }, 0)

  // Range +/- 20%
  const minCost = Math.round(totalCost * 0.8)
  const maxCost = Math.round(totalCost * 1.2)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (email) {
      setSubmitted(true)
      // Call your API here to send the lead
    }
  }

  return (
    <section className="py-24 bg-secondary/50" id="hesaplayici">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12">
          <span className="text-brand font-semibold tracking-wider uppercase text-sm">ÜCRETSİZ ARAÇ</span>
          <h2 className="text-3xl md:text-5xl font-bold mt-4 mb-4">Projenizin Maliyetini Hesaplayın</h2>
          <p className="text-muted-foreground text-lg">
            Hemen tahmini bir bütçe aralığı görün ve teknik detaylar için teklif isteyin.
          </p>
        </div>

        <div className="bg-background rounded-[2rem] p-8 shadow-xl border border-border">
          <div className="space-y-8">
            {/* Platform Selection */}
            <div>
              <h3 className="text-xl font-bold mb-4">1. Projenin Türü Nedir?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {PLATFORMS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setPlatform(p)}
                    className={`p-4 rounded-xl border text-left transition-all flex justify-between items-center ${
                      platform.id === p.id 
                      ? 'border-brand bg-brand/5 ring-1 ring-brand' 
                      : 'border-border hover:border-brand/50'
                    }`}
                  >
                    <span className={`font-medium ${platform.id === p.id ? 'text-brand' : 'text-foreground'}`}>
                      {p.name}
                    </span>
                    <span className="text-xs font-mono bg-secondary px-2 py-1 rounded-md text-muted-foreground">
                      ${p.basePrice.toLocaleString('en-US')}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Features Selection */}
            <div>
              <h3 className="text-xl font-bold mb-4">2. Hangi Özelliklere İhtiyacınız Var?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FEATURES.map(f => {
                  const isSelected = selectedFeatures.includes(f.id)
                  return (
                    <button
                      key={f.id}
                      onClick={() => toggleFeature(f.id)}
                      className={`p-4 rounded-xl border text-left flex items-center justify-between transition-all ${
                        isSelected 
                        ? 'border-brand bg-brand/5 ring-1 ring-brand' 
                        : 'border-border hover:border-brand/50'
                      }`}
                    >
                      <div className="flex flex-col">
                        <span className={`font-medium ${isSelected ? 'text-brand' : 'text-foreground'}`}>
                          {f.name}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground mt-1">
                          +${f.price.toLocaleString('en-US')}
                        </span>
                      </div>
                      <div className={`w-5 h-5 rounded border flex shrink-0 items-center justify-center ${isSelected ? 'bg-brand border-brand' : 'border-muted-foreground'}`}>
                        {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="pt-8 border-t border-border">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div>
                  <p className="text-muted-foreground font-medium mb-2">Tahmini Bütçe Aralığı:</p>
                  <p className="text-4xl font-bold font-mono text-brand">
                    ${minCost.toLocaleString('en-US')} - ${maxCost.toLocaleString('en-US')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">*Tahmini fiyatlandırmadır. Vergi hariçtir.</p>
                </div>
                
                <div className="w-full md:w-auto">
                  {!submitted ? (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                      <p className="text-sm font-medium">Detaylı teklif için e-posta bırakın:</p>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="hello@fiveline.dev"
                          className="px-4 py-3 rounded-lg border border-border bg-background flex-1"
                        />
                        <Button type="submit" size="lg" className="bg-brand text-primary-foreground hover:bg-brand/90">
                          Gönder
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="bg-green-500/10 text-green-600 border border-green-500/20 p-4 rounded-xl text-center font-medium">
                      Talebiniz alındı! En kısa sürede sizinle iletişime geçeceğiz.
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  )
}
